import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  ActionItemSummary,
  ActionPriority,
  AnswerClarificationsRequest,
  CompleteActionWithContextRequest,
  CompleteActionWithContextResponse,
  DraftReplyResponse,
  SetActionStateRequest,
  SignalCaptureRequest,
  SignalDetail,
  SignalSummary,
  TranscribeResponse,
  WaitingOnSummary,
} from '../models/pipeline';
import { ActionStatus, SignalStatus } from '../models/pipeline';
import { Workspace, DomainArea } from '../models/prospect';

@Injectable({ providedIn: 'root' })
export class PipelineService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiBaseUrl.replace(/\/+$/, '');

  // Local caches for the realtime hub to push into.
  private readonly _signals = signal<SignalSummary[]>([]);
  private readonly _actions = signal<ActionItemSummary[]>([]);
  private readonly _waiting = signal<WaitingOnSummary[]>([]);

  readonly signals = this._signals.asReadonly();
  readonly actions = this._actions.asReadonly();
  readonly waiting = this._waiting.asReadonly();

  // Last-used load arguments, remembered so a SignalR reconnect can replay
  // exactly what each surface asked for. Hub events missed while offline would
  // otherwise leave these caches silently stale until the next navigation.
  private lastSignalFilters?: Parameters<PipelineService['loadSignals']>[0];
  private lastActionFilters?: Parameters<PipelineService['loadActions']>[0];
  private lastWaitingIncludeResolved?: boolean;

  /**
   * Re-fetch whichever caches have been populated this session, using the same
   * filters the owning page last requested. Called by RealtimeService after a
   * reconnect; failures are tolerated (the next user action re-loads anyway).
   */
  async resync(): Promise<void> {
    const jobs: Promise<unknown>[] = [];
    if (this.lastSignalFilters !== undefined) jobs.push(this.loadSignals(this.lastSignalFilters));
    if (this.lastActionFilters !== undefined) jobs.push(this.loadActions(this.lastActionFilters));
    if (this.lastWaitingIncludeResolved !== undefined) jobs.push(this.loadWaiting(this.lastWaitingIncludeResolved));
    await Promise.allSettled(jobs);
  }

  // -------- Signals --------

  async loadSignals(filters: {
    status?: SignalStatus;
    workspace?: Workspace;
    domain?: DomainArea;
  } = {}): Promise<SignalSummary[]> {
    this.lastSignalFilters = filters;
    let p = new HttpParams();
    if (filters.status) p = p.set('status', filters.status);
    if (filters.workspace) p = p.set('workspace', filters.workspace);
    if (filters.domain) p = p.set('domain', filters.domain);
    const list = await firstValueFrom(
      this.http.get<SignalSummary[]>(`${this.base}/api/signals`, { params: p }));
    this._signals.set(list);
    return list;
  }

  async getSignal(id: string): Promise<SignalDetail> {
    return await firstValueFrom(
      this.http.get<SignalDetail>(`${this.base}/api/signals/${id}`));
  }

  async capture(req: SignalCaptureRequest): Promise<SignalDetail> {
    const detail = await firstValueFrom(
      this.http.post<SignalDetail>(`${this.base}/api/signals`, req));
    this.applySignalChange(this.toSummary(detail));
    return detail;
  }

  async triage(id: string): Promise<SignalDetail> {
    const detail = await firstValueFrom(
      this.http.post<SignalDetail>(`${this.base}/api/signals/${id}/triage`, null));
    this.applySignalChange(this.toSummary(detail));
    return detail;
  }

  async answer(id: string, req: AnswerClarificationsRequest): Promise<SignalDetail> {
    const detail = await firstValueFrom(
      this.http.post<SignalDetail>(`${this.base}/api/signals/${id}/answers`, req));
    this.applySignalChange(this.toSummary(detail));
    return detail;
  }

  async materialize(id: string): Promise<SignalDetail> {
    const detail = await firstValueFrom(
      this.http.post<SignalDetail>(`${this.base}/api/signals/${id}/materialize`, null));
    this.applySignalChange(this.toSummary(detail));
    detail.actionItems.forEach(a => this.applyActionChange(a));
    return detail;
  }

  async discard(id: string): Promise<void> {
    await firstValueFrom(
      this.http.post<void>(`${this.base}/api/signals/${id}/discard`, null));
    this._signals.update(list => list.filter(s => s.id !== id));
  }

  /**
   * Phase 1.5 (Triage UI Step D) — ask the server to draft a reply to this
   * signal in Chad's voice, using his answered clarification questions as
   * context. Returns the editable body; sending is handled separately (and
   * currently gated on the Mail.Send Graph scope).
   */
  async draftReply(id: string): Promise<DraftReplyResponse> {
    return await firstValueFrom(
      this.http.post<DraftReplyResponse>(`${this.base}/api/signals/${id}/draft-reply`, null));
  }

  // -------- Action items --------

  async loadActions(filters: {
    status?: ActionStatus;
    workspace?: Workspace;
    domain?: DomainArea;
  } = {}): Promise<ActionItemSummary[]> {
    this.lastActionFilters = filters;
    let p = new HttpParams();
    if (filters.status) p = p.set('status', filters.status);
    if (filters.workspace) p = p.set('workspace', filters.workspace);
    if (filters.domain) p = p.set('domain', filters.domain);
    const list = await firstValueFrom(
      this.http.get<ActionItemSummary[]>(`${this.base}/api/actions`, { params: p }));
    this._actions.set(list);
    return list;
  }

  async setActionState(id: string, req: SetActionStateRequest): Promise<ActionItemSummary> {
    const updated = await firstValueFrom(
      this.http.post<ActionItemSummary>(`${this.base}/api/actions/${id}/state`, req));
    this.applyActionChange(updated);
    return updated;
  }

  /**
   * Interaction-craft A2 — inline edit of a row's content (title / detail /
   * estimate). Optimistic: patch the cached row immediately, PATCH the server,
   * reconcile with the returned summary; roll back + rethrow on failure.
   */
  async updateAction(
    id: string,
    patch: { title?: string; detail?: string; estimatedMinutes?: number },
  ): Promise<ActionItemSummary> {
    const prev = this._actions();
    this._actions.update(list =>
      list.map(a => (a.id === id ? { ...a, ...patch } as ActionItemSummary : a)));
    try {
      const updated = await firstValueFrom(
        this.http.patch<ActionItemSummary>(`${this.base}/api/actions/${id}`, patch));
      this.applyActionChange(updated);
      return updated;
    } catch (e) {
      this._actions.set(prev);
      throw e;
    }
  }

  /**
   * Interaction-craft A1 — persist a manual within-band reorder. `orderedIds` is
   * the full id-list for one priority band in its new order. Updates the local
   * cache optimistically so the UI reflects the move immediately, then POSTs;
   * rolls the cache back and re-throws if the server rejects.
   */
  async reorderActions(orderedIds: string[]): Promise<void> {
    const prev = this._actions();
    this.applyReorderLocal(orderedIds);
    try {
      await firstValueFrom(
        this.http.post<void>(`${this.base}/api/actions/reorder`, { orderedIds }));
    } catch (e) {
      this._actions.set(prev);
      throw e;
    }
  }

  /**
   * Reorder the flat actions cache so the given band's items appear in the new
   * relative order, leaving every other item in its existing slot. The grouped()
   * view in the component buckets by priority in arrival order, so slotting the
   * band's items back in `orderedIds` order is enough to re-render correctly.
   */
  private applyReorderLocal(orderedIds: string[]): void {
    const idSet = new Set(orderedIds);
    this._actions.update(list => {
      const reordered = orderedIds
        .map(id => list.find(a => a.id === id))
        .filter((a): a is ActionItemSummary => !!a);
      let ri = 0;
      return list.map(a => (idSet.has(a.id) ? reordered[ri++] : a));
    });
  }

  /**
   * Interaction-craft A1.5 — cross-band drag = re-prioritize. `destIds`/`srcIds`
   * are the FULL ordered id-lists of the destination and source bands AFTER the
   * move (destIds includes movedId at its new slot; srcIds excludes it). Updates
   * the cache optimistically (re-priorities the moved item + reorders both bands),
   * POSTs, rolls back + re-throws on failure.
   */
  async moveAction(
    movedId: string,
    toPriority: ActionPriority,
    destIds: string[],
    srcIds: string[],
  ): Promise<void> {
    const prev = this._actions();
    this.applyMoveLocal(movedId, toPriority, destIds, srcIds);
    try {
      await firstValueFrom(this.http.post<void>(`${this.base}/api/actions/move`, {
        actionId: movedId,
        toPriority,
        destinationOrderedIds: destIds,
        sourceOrderedIds: srcIds,
      }));
    } catch (e) {
      this._actions.set(prev);
      throw e;
    }
  }

  private applyMoveLocal(
    movedId: string,
    toPriority: ActionPriority,
    destIds: string[],
    srcIds: string[],
  ): void {
    this._actions.update(list => {
      // Re-priority the moved item so grouped() re-buckets it into the dest band.
      const withPriority = list.map(a => (a.id === movedId ? { ...a, priority: toPriority } : a));
      const byId = new Map(withPriority.map(a => [a.id, a]));
      const affected = new Set<string>([...destIds, ...srcIds]);
      // destIds / srcIds are FULL band lists, so every affected-band item is in
      // one of them; emit those in the new order, others keep their slots. The
      // grouped() view buckets by priority, so cross-band interleaving is moot.
      const destItems = destIds.map(id => byId.get(id)).filter((a): a is ActionItemSummary => !!a);
      const srcItems = srcIds.map(id => byId.get(id)).filter((a): a is ActionItemSummary => !!a);
      const others = withPriority.filter(a => !affected.has(a.id));
      return [...others, ...destItems, ...srcItems];
    });
  }

  /**
   * Phase 22 — complete an action with the user's captured outreach context
   * (channel, sentiment, gist, free notes). Returns the smart next-touch
   * recommendation alongside the completed-action summary so the SPA can
   * render a confirmation toast with links to the new snoozed action and
   * the AI Suggestion Signal carrying the reasoning.
   */
  async completeWithContext(
    id: string,
    req: CompleteActionWithContextRequest,
  ): Promise<CompleteActionWithContextResponse> {
    const resp = await firstValueFrom(
      this.http.post<CompleteActionWithContextResponse>(
        `${this.base}/api/actions/${id}/complete-with-context`, req));
    this.applyActionChange(resp.completedAction);
    return resp;
  }

  /**
   * Phase 22 — transcribe an audio blob without persisting a Signal. Powers
   * the voice-input button on the Done-with-context modal: user dictates
   * "texted Chuck about his daughter's wedding", we fill the gist field with
   * the transcript so they can edit and submit.
   */
  async transcribeVoice(audio: Blob, fileName: string): Promise<TranscribeResponse> {
    const form = new FormData();
    form.append('file', audio, fileName);
    return await firstValueFrom(
      this.http.post<TranscribeResponse>(`${this.base}/api/captures/transcribe-voice`, form));
  }

  async deleteAction(id: string): Promise<void> {
    await firstValueFrom(this.http.delete<void>(`${this.base}/api/actions/${id}`));
    this._actions.update(list => list.filter(a => a.id !== id));
  }

  // -------- Waiting-on --------

  async loadWaiting(includeResolved = false): Promise<WaitingOnSummary[]> {
    this.lastWaitingIncludeResolved = includeResolved;
    const p = new HttpParams().set('includeResolved', String(includeResolved));
    const list = await firstValueFrom(
      this.http.get<WaitingOnSummary[]>(`${this.base}/api/waiting-on`, { params: p }));
    this._waiting.set(list);
    return list;
  }

  async resolveWaiting(id: string, abandoned = false): Promise<void> {
    const p = new HttpParams().set('abandoned', String(abandoned));
    await firstValueFrom(
      this.http.post<void>(`${this.base}/api/waiting-on/${id}/resolve`, null, { params: p }));
    this._waiting.update(list => list.filter(w => w.id !== id));
  }

  // -------- Realtime hooks --------

  applySignalChange(s: SignalSummary): void {
    this._signals.update(list => {
      const idx = list.findIndex(x => x.id === s.id);
      if (idx === -1) return [s, ...list];
      const copy = [...list];
      copy[idx] = s;
      return copy;
    });
  }

  applySignalDeleted(id: string): void {
    this._signals.update(list => list.filter(s => s.id !== id));
  }

  applyActionChange(a: ActionItemSummary): void {
    this._actions.update(list => {
      const idx = list.findIndex(x => x.id === a.id);
      if (idx === -1) return [a, ...list];
      const copy = [...list];
      copy[idx] = a;
      return copy;
    });
  }

  applyActionDeleted(id: string): void {
    this._actions.update(list => list.filter(a => a.id !== id));
  }

  applyWaitingChange(w: WaitingOnSummary): void {
    this._waiting.update(list => {
      const idx = list.findIndex(x => x.id === w.id);
      if (idx === -1) return [w, ...list];
      const copy = [...list];
      copy[idx] = w;
      return copy;
    });
  }

  private toSummary(d: SignalDetail): SignalSummary {
    return {
      id: d.id,
      source: d.source,
      status: d.status,
      workspace: d.workspace,
      domain: d.domain,
      rawContentPreview: d.rawContent.length > 240
        ? d.rawContent.slice(0, 240) + '…'
        : d.rawContent,
      summary: d.summary,
      subjectId: d.subjectId,
      subjectName: d.subjectName,
      outstandingQuestions: d.questions.filter(q => !q.isAnswered).length,
      materializedActions: d.actionItems.length,
      capturedAt: d.capturedAt,
      priority: d.priority,
      suggestedAction: d.suggestedAction,
    };
  }
}
